import * as bootstrap from 'bootstrap';
import { get, post, notifier } from '../util';
import * as Highcharts from 'highcharts';

const ALC_COLOR_ESTIMATE: Highcharts.GradientColorObject = {
    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
    stops: [
        [0, '#E1A818'],
        [0.1, 'rgba(59, 171, 211, 0.45)'],
        [0.9, 'rgba(59, 171, 211, 0.45)'],
        [1, '#E1A818'],
    ],
};

// Module-level state
let modal: bootstrap.Modal | null = null;
let currentChartType: string = '';   // original id passed to openModal ('alc'|'weight'|'waist'|'gym')
let currentDate: string = '';
let isEdit: boolean = false;

const GYM_COLUMNS = ['chest', 'biceps', 'back', 'triceps', 'legs', 'shoulders', 'core', 'forearms', 'rock', 'cardio'] as const;
const GYM_LABELS  = ['Chest', 'Biceps', 'Back', 'Triceps', 'Legs', 'Shoulders', 'Core', 'Forearms', 'Rock Climbing', 'Cardio'] as const;

export function initModal(): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    modal = new bootstrap.Modal(modalEl, { backdrop: true, keyboard: true });

    const saveBtn = modalEl.querySelector('.js-modal-save') as HTMLButtonElement;
    const deleteBtn = modalEl.querySelector('.js-modal-delete') as HTMLButtonElement;

    if (saveBtn) {
        saveBtn.addEventListener('click', () => saveModal());
    }
    if (deleteBtn) {
        deleteBtn.addEventListener('click', () => deleteModal());
    }
}

export function openModal(chartType: string, date: string): void {
    if (!modal) return;

    currentChartType = chartType;
    currentDate = date;

    // Determine form type: waist maps to weight form
    const formType = chartType === 'waist' ? 'weight' : chartType;

    get('?do=getValuesFromDate&date=' + date, {}, (data: any) => {
        // Determine edit vs add mode based on chart type
        let hasData = false;
        if (formType === 'alc') {
            hasData = data.alc !== null && data.alc !== undefined;
        } else if (formType === 'weight') {
            hasData = data.weight !== null || data.waist !== null;
        } else if (formType === 'gym') {
            hasData = data.gym !== null && data.gym !== undefined;
        } else if (formType === 'snap') {
            hasData = data.snap !== null && data.snap !== undefined;
        }
        isEdit = hasData;

        // Set modal title
        const typeLabel = formType === 'alc' ? 'Alcohol' : formType === 'weight' ? 'Weight & Waist' : formType === 'snap' ? 'Snap Score' : 'Gym';
        const modalTitle = document.getElementById('chart-edit-modal-title');
        if (modalTitle) {
            modalTitle.textContent = (isEdit ? 'Edit ' : 'Add ') + typeLabel + ' \u2014 ' + date;
        }

        // Show/hide delete button
        const modalEl = document.getElementById('chart-edit-modal')!;
        const deleteBtn = modalEl.querySelector('.js-modal-delete') as HTMLButtonElement;
        if (deleteBtn) {
            deleteBtn.classList.toggle('d-none', !isEdit);
        }

        // Populate modal body
        const body = document.getElementById('chart-edit-modal-body');
        if (!body) return;
        body.innerHTML = '';

        if (formType === 'alc') {
            const qty = isEdit ? (data.alc ?? '') : '';
            const estimateChecked = isEdit && data.alc_estimate ? 'checked' : '';
            body.innerHTML = `
                <div class="mb-3">
                    <div class="d-flex align-items-end gap-3">
                        <div>
                            <label class="form-label">Quantity</label>
                            <input type="number" class="form-control" id="modal-alc-qty" step="any" inputmode="decimal" value="${qty}" />
                        </div>
                        <div class="form-check form-check-inline mb-2">
                            <input class="form-check-input" type="checkbox" id="modal-alc-estimate" ${estimateChecked} />
                            <label class="form-check-label" for="modal-alc-estimate">Estimate</label>
                        </div>
                    </div>
                </div>
            `;
        } else if (formType === 'weight') {
            const weight = isEdit ? (data.weight ?? '') : '';
            const waist  = isEdit ? (data.waist ?? '') : '';
            body.innerHTML = `
                <div class="row g-2">
                    <div class="col-6">
                        <label class="form-label">Weight (lbs)</label>
                        <input type="number" class="form-control" id="modal-weight" step="any" inputmode="decimal" value="${weight}" />
                    </div>
                    <div class="col-6">
                        <label class="form-label">Waist (cm)</label>
                        <input type="number" class="form-control" id="modal-waist" step="any" inputmode="decimal" value="${waist}" />
                    </div>
                </div>
            `;
        } else if (formType === 'gym') {
            const gymData = isEdit ? data.gym : null;
            const grid = document.createElement('div');
            grid.style.display = 'grid';
            grid.style.gridTemplateColumns = 'repeat(2, 1fr)';
            grid.style.gap = '6px 12px';

            GYM_COLUMNS.forEach((col, i) => {
                const checked = gymData && gymData[col] ? 'checked' : '';
                const wrapper = document.createElement('div');
                wrapper.className = 'form-check';
                wrapper.innerHTML = `
                    <input class="form-check-input" type="checkbox" id="modal-gym-${col}" data-col="${col}" ${checked} />
                    <label class="form-check-label" for="modal-gym-${col}">${GYM_LABELS[i]}</label>
                `;
                grid.appendChild(wrapper);
            });

            body.appendChild(grid);
        } else if (formType === 'snap') {
            const score = isEdit ? (data.snap ?? '') : '';
            const estimateChecked = isEdit && data.snap_est ? 'checked' : '';
            body.innerHTML = `
                <div class="mb-3">
                    <div class="d-flex align-items-end gap-3">
                        <div>
                            <label class="form-label">Score</label>
                            <input type="number" class="form-control" id="modal-snap-score" step="1" inputmode="numeric" value="${score}" />
                        </div>
                        <div class="form-check form-check-inline mb-2">
                            <input class="form-check-input" type="checkbox" id="modal-snap-est" ${estimateChecked} />
                            <label class="form-check-label" for="modal-snap-est">Estimate</label>
                        </div>
                    </div>
                </div>
            `;
        }

        modal!.show();
    });
}

export function saveModal(): void {
    if (!modal) return;

    const modalEl = document.getElementById('chart-edit-modal')!;
    const saveBtn = modalEl.querySelector('.js-modal-save') as HTMLButtonElement;
    const saveLabel = modalEl.querySelector('.js-modal-save-label') as HTMLElement;
    const spinner = modalEl.querySelector('.js-modal-spinner') as HTMLElement;

    // Disable button and show spinner
    saveBtn.disabled = true;
    saveLabel.classList.add('d-none');
    spinner.classList.remove('d-none');

    // Remove any previous inline error
    const prevError = modalEl.querySelector('.modal-inline-error');
    if (prevError) prevError.remove();

    const formType = currentChartType === 'waist' ? 'weight' : currentChartType;
    let fields: Record<string, string | number> = {};

    if (formType === 'alc') {
        const input = document.getElementById('modal-alc-qty') as HTMLInputElement;
        const estimateCheckbox = document.getElementById('modal-alc-estimate') as HTMLInputElement;
        fields = { qty: input ? input.value : '', estimate: estimateCheckbox && estimateCheckbox.checked ? 1 : 0 };
    } else if (formType === 'weight') {
        const wInput = document.getElementById('modal-weight') as HTMLInputElement;
        const waistInput = document.getElementById('modal-waist') as HTMLInputElement;
        fields = {
            weight: wInput ? wInput.value : '',
            waist: waistInput ? waistInput.value : ''
        };
    } else if (formType === 'gym') {
        GYM_COLUMNS.forEach(col => {
            const cb = document.querySelector(`#modal-gym-${col}`) as HTMLInputElement;
            fields[col] = cb && cb.checked ? 1 : 0;
        });
    } else if (formType === 'snap') {
        const scoreInput = document.getElementById('modal-snap-score') as HTMLInputElement;
        const estCheckbox = document.getElementById('modal-snap-est') as HTMLInputElement;
        fields = { score: scoreInput ? scoreInput.value : '', est: estCheckbox && estCheckbox.checked ? 1 : 0 };
    }

    post('?do=updateValue&type=' + formType + '&date=' + currentDate, fields, (data: any) => {
        // Always re-enable save button
        saveBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        if (data.status === 1) {
            updateChartPoint(formType, fields);
            modal!.hide();
            notifier.success('Saved');
        } else {
            // Show inline error
            const errorEl = document.createElement('div');
            errorEl.className = 'text-danger small mt-2 modal-inline-error';
            errorEl.textContent = data.message || 'An error occurred. Please try again.';
            const body = document.getElementById('chart-edit-modal-body');
            if (body) body.appendChild(errorEl);
        }
    });
}

export function deleteModal(): void {
    if (!modal) return;

    if (!window.confirm('Delete this entry?')) return;

    const modalEl = document.getElementById('chart-edit-modal')!;
    const deleteBtn = modalEl.querySelector('.js-modal-delete') as HTMLButtonElement;
    deleteBtn.disabled = true;

    // Remove any previous inline error
    const prevError = modalEl.querySelector('.modal-inline-error');
    if (prevError) prevError.remove();

    const formType = currentChartType === 'waist' ? 'weight' : currentChartType;

    post('?do=deleteValue&type=' + formType + '&date=' + currentDate, {}, (data: any) => {
        deleteBtn.disabled = false;

        if (data.status === 1) {
            nullChartPoint(formType);
            modal!.hide();
            notifier.success('Deleted');
        } else {
            const errorEl = document.createElement('div');
            errorEl.className = 'text-danger small mt-2 modal-inline-error';
            errorEl.textContent = data.message || 'An error occurred. Please try again.';
            const body = document.getElementById('chart-edit-modal-body');
            if (body) body.appendChild(errorEl);
        }
    });
}

function getPointIndex(series: Highcharts.Series, date: string): number {
    //@ts-ignore
    const pointStart = series.options.pointStart as number;
    // Use local time to match pointStart which is Date.parse(dates_array[0]) (local)
    const clickedTimestamp = Date.parse(date);
    return Math.round((clickedTimestamp - pointStart) / 86400000);
}

function findChart(chartId: string): Highcharts.Chart | undefined {
    //@ts-ignore
    return Highcharts.charts.find((c: any) => c?.renderTo?.id === chartId);
}

function updateChartPoint(formType: string, fields: Record<string, string | number>): void {
    if (formType === 'snap') { refreshSnapChart(); return; }
    if (formType === 'alc') {
        const chart = findChart('chart_alc');
        if (!chart) return;
        const series = chart.series[0];
        const idx = getPointIndex(series, currentDate);
        const point = series.data[idx];
        if (point !== undefined) {
            const isEstimate = fields.estimate === 1;
            //@ts-ignore
            point.update({
                y: parseFloat(fields.qty as string) * -1,
                color: isEstimate ? ALC_COLOR_ESTIMATE : series.color,
                borderWidth: isEstimate ? 1 : 0,
                dashStyle: isEstimate ? 'ShortDash' : 'Solid',
                custom: { estimate: isEstimate ? 1 : 0 },
            });
        }
    } else if (formType === 'weight') {
        // Update weight chart
        const weightChart = findChart('chart_weight');
        if (weightChart) {
            const series = weightChart.series[0];
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            if (point !== undefined) {
                //@ts-ignore
                point.update(parseFloat(fields.weight as string) || null);
            }
        }
        // Update waist chart
        const waistChart = findChart('chart_waist');
        if (waistChart) {
            const series = waistChart.series[0];
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            if (point !== undefined) {
                //@ts-ignore
                point.update(parseFloat(fields.waist as string) || null);
            }
        }
    } else if (formType === 'gym') {
        const chart = findChart('chart_gym');
        if (!chart) return;
        GYM_COLUMNS.forEach((col, i) => {
            const series = chart.series[i];
            if (!series) return;
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            if (point !== undefined) {
                //@ts-ignore
                point.update(fields[col] ? 1 : null);
            }
        });
    }
}

function nullChartPoint(formType: string): void {
    if (formType === 'snap') { refreshSnapChart(); return; }
    if (formType === 'alc') {
        const chart = findChart('chart_alc');
        if (!chart) return;
        const series = chart.series[0];
        const idx = getPointIndex(series, currentDate);
        const point = series.data[idx];
        //@ts-ignore
        if (point !== undefined) point.update(null);
    } else if (formType === 'weight') {
        ['chart_weight', 'chart_waist'].forEach(chartId => {
            const chart = findChart(chartId);
            if (!chart) return;
            const series = chart.series[0];
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            //@ts-ignore
            if (point !== undefined) point.update(null);
        });
    } else if (formType === 'gym') {
        const chart = findChart('chart_gym');
        if (!chart) return;
        chart.series.forEach(series => {
            const idx = getPointIndex(series, currentDate);
            const point = series.data[idx];
            //@ts-ignore
            if (point !== undefined) point.update(null);
        });
    }
}

/**
 * Refresh the Snapchat chart after a snap add/edit/delete — no page reload.
 *
 * Gap interpolation makes the series interdependent across an entire gap (changing one
 * reading re-interpolates every estimate between it and its neighbours), so instead of
 * patching points client-side we re-fetch the authoritative gap-interpolated series from
 * the server (?do=getSnapSeries) and swap both series' data in place. Single source of truth.
 */
function refreshSnapChart(): void {
    const chart = findChart('chart_snap');
    if (!chart) return;
    post('?do=getSnapSeries', {}, (resp: any) => {
        if (!resp || resp.success !== true || !resp.series || !resp.series.data) return;
        const data = resp.series.data;
        //@ts-ignore
        if (chart.series[0] && data[0]) chart.series[0].setData(data[0], false);
        //@ts-ignore
        if (chart.series[1] && data[1]) chart.series[1].setData(data[1], false);
        chart.redraw();
        updateSnapTotals(resp.series.totals);
    });
}

/** Update the Score / Gained totals above the snap chart from a fresh server payload. */
function updateSnapTotals(totals: Array<{ name: string; value: string }> | undefined): void {
    const wrapper = document.querySelector('.chart-wrapper[data-id="snap"]');
    if (!wrapper || !totals) return;
    wrapper.querySelectorAll('.chart-total').forEach((t) => {
        const label = t.querySelector('.chart-total-label')?.textContent?.trim().replace(/:$/, '');
        const valEl = t.querySelector('.chart-total-value');
        if (!valEl) return;
        const match = totals.find((x) => x.name === label);
        if (match) valEl.textContent = match.value;
    });
}
